import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Chip, Button, Skeleton,
  Alert, IconButton, Collapse, Divider, Tooltip, Link,
} from '@mui/material';
import {
  Event as EventIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
  AutoAwesome as SuggestIcon,
  LocationOn as LocationIcon,
  CalendarMonth as CalendarIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import RsvpButton from 'components/collab/events/RsvpButton';
import EventFormDialog from 'components/collab/events/EventFormDialog';
import { formatDateRange } from 'components/collab/events/eventMeta';

// The ProposalWriter is a full-screen overlay: menus and dialogs opened from here go above it
const OVERLAY_Z = 3200;

export default function EventSuggestionCard({ callId }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const replaceEvent = (updated) => setEvents(prev => prev.map(e => (e.id === updated.id ? { ...e, ...updated } : e)));

  useEffect(() => {
    if (!callId) return;
    setLoading(true);
    setError(null);
    axiosService.get('/events/suggested/', { params: { call_id: callId } })
      .then(res => setEvents(res.data || []))
      .catch(() => setError('Could not load event suggestions.'))
      .finally(() => setLoading(false));
  }, [callId, reload]);

  if (!callId) return null;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setExpanded(e => !e)}
      >
        <SuggestIcon color="secondary" sx={{ mr: 1 }} />
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
          Suggested Events
        </Typography>
        {!loading && (
          <Chip label={events.length} size="small" color={events.length > 0 ? 'secondary' : 'default'} sx={{ mr: 1 }} />
        )}
        <Tooltip title="Create an event">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setCreateOpen(true); }}
            sx={{ mr: 0.5 }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Browse all events">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); navigate('/events'); }}
            sx={{ mr: 0.5 }}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Upcoming events matching this call's research areas
          </Typography>
        </Box>

        {loading && (
          <Stack spacing={1} sx={{ px: 2, pb: 2 }}>
            {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={56} />)}
          </Stack>
        )}

        {error && (
          <Alert severity="warning" sx={{ mx: 2, mb: 2 }}>{error}</Alert>
        )}

        {!loading && !error && events.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
            No matching upcoming events found. Public events tagged with this call's research areas appear here.
          </Typography>
        )}

        {!loading && !error && events.length > 0 && (
          <Stack spacing={1} sx={{ px: 2, pb: 2 }}>
            {events.map(event => (
              <Paper key={event.id} variant="outlined"
                sx={{ p: 1.5, borderRadius: 1, '&:hover': { borderColor: 'primary.main' } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <EventIcon color="primary" sx={{ mt: 0.25, fontSize: 18 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Link component="button" variant="body2" fontWeight={600} underline="hover" color="text.primary"
                        onClick={() => navigate(`/events/${event.id}/`)}
                        sx={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {event.title}
                      </Link>
                      {event.website && (
                        <Tooltip title="Open event website">
                          <IconButton size="small" component="a" href={event.website} target="_blank" rel="noopener noreferrer">
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 0.75 }}>
                      {event.start_date && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {formatDateRange(event.start_date, event.end_date)}
                          </Typography>
                        </Box>
                      )}
                      {(event.location || event.is_online) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {event.is_online ? 'Online' : event.location}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      <Chip label={event.event_type_display} size="small" variant="outlined" />
                      {(event.categories || []).slice(0, 3).map(cat => (
                        <Chip key={cat.id} label={cat.name} size="small" color="secondary" variant="outlined" />
                      ))}
                      {event.categories?.length > 3 && (
                        <Chip label={`+${event.categories.length - 3}`} size="small" variant="outlined" />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <RsvpButton event={event} onChange={replaceEvent} menuZ={OVERLAY_Z + 100} />
                      {(event.going_count > 0 || event.interested_count > 0) && (
                        <Typography variant="caption" color="text.secondary">
                          {[event.going_count > 0 && `${event.going_count} going`,
                            event.interested_count > 0 && `${event.interested_count} interested`].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
        )}
      </Collapse>
      <EventFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        zIndex={OVERLAY_Z}
        onSaved={() => setReload(r => r + 1)}
      />
    </Paper>
  );
}
